/**
 * Impersonation Hook — Allows admin to view/act as any client
 *
 * Provides ImpersonationContext and useEffectiveClientId() convenience hook.
 * When impersonating, all client-facing hooks and Cloud Function calls
 * use the impersonated clientId instead of auth.uid.
 *
 * State is persisted to localStorage so impersonation survives page reloads,
 * new tabs, and hard URL navigations (e.g. when the admin opens the Leads
 * upload page from anywhere). It is only cleared by an explicit
 * stopImpersonation() call. Cloud Functions always re-verify admin role
 * server-side via resolveClientId, so persisting client-side context cannot
 * be used by a non-admin to escalate.
 */

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useAuth } from './useAuth';

const LS_CLIENT_ID = 'jad_impersonation_clientId';
const LS_CLIENT_NAME = 'jad_impersonation_clientName';

interface ImpersonationContextType {
  impersonatedClientId: string | null;
  impersonatedClientName: string | null;
  isImpersonating: boolean;
  startImpersonation: (clientId: string, clientName: string) => void;
  stopImpersonation: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextType | null>(null);

function readPersisted(): { id: string | null; name: string | null } {
  if (typeof window === 'undefined') return { id: null, name: null };
  try {
    return {
      id: window.localStorage.getItem(LS_CLIENT_ID),
      name: window.localStorage.getItem(LS_CLIENT_NAME),
    };
  } catch {
    // localStorage can throw in private-mode / disabled-storage browsers.
    return { id: null, name: null };
  }
}

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  // Lazy initializer reads localStorage exactly once on mount so a hard reload
  // mid-impersonation restores the prior state instead of dropping back to
  // user.uid (which would silently route writes to the admin's collection).
  const [impersonatedClientId, setImpersonatedClientId] = useState<string | null>(
    () => readPersisted().id
  );
  const [impersonatedClientName, setImpersonatedClientName] = useState<string | null>(
    () => readPersisted().name
  );

  const startImpersonation = useCallback((clientId: string, clientName: string) => {
    setImpersonatedClientId(clientId);
    setImpersonatedClientName(clientName);
    try {
      window.localStorage.setItem(LS_CLIENT_ID, clientId);
      window.localStorage.setItem(LS_CLIENT_NAME, clientName);
    } catch { /* swallow private-mode failures */ }
  }, []);

  const stopImpersonation = useCallback(() => {
    setImpersonatedClientId(null);
    setImpersonatedClientName(null);
    try {
      window.localStorage.removeItem(LS_CLIENT_ID);
      window.localStorage.removeItem(LS_CLIENT_NAME);
    } catch { /* swallow */ }
  }, []);

  const isImpersonating = impersonatedClientId !== null;

  return (
    <ImpersonationContext.Provider value={{
      impersonatedClientId,
      impersonatedClientName,
      isImpersonating,
      startImpersonation,
      stopImpersonation,
    }}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
}

/**
 * Returns the effective client ID for data access.
 * If admin is impersonating, returns the impersonated client's ID.
 * Otherwise returns the authenticated user's UID.
 */
export function useEffectiveClientId(): string | null {
  const { user } = useAuth();
  const { impersonatedClientId } = useImpersonation();
  return impersonatedClientId || user?.uid || null;
}
